import React, { useEffect, useState, useCallback } from 'react';
import api from '../services/ApiService';

// ── Types ────────────────────────────────────────────────────────────────────

type Phone = {
  id: number;
  tenant_id: number;
  tenant_name?: string;
  device_name: string;
  mac_address: string;
  model: string;
  extension_id?: number;
  extension_number?: string;
  extension_display?: string;
  provisioning_enabled: boolean;
  firmware_version?: string;
  last_checkin?: string;
  sip_registered?: boolean;
  sip_state?: string;
  sip_contact_ip?: string | null;
  sip_rtt_ms?: number | null;
  created_at: string;
  updated_at: string;
};

type Extension = {
  id: number;
  extension_number: string;
  display_name: string;
  tenant_id: number;
};

type Tenant = { id: number; tenant_name: string };

type Dashboard = {
  totals: { total: number; provisioned: number; recently_seen: number; never_checked_in: number };
  by_model: { model: string; count: number }[];
  by_firmware: { firmware_version: string; count: number }[];
  by_tenant: { tenant_name: string; count: number }[];
  recent_checkins: Phone[];
};

type PhoneModel = { model: string; load_information: string; lines: number };
type FirmwareRow = { id: number; model: string; firmware_version: string; file_name: string; file_size?: number };
type AuditRow = { id: number; action: string; ip_address?: string; details?: any; created_at: string; username?: string };

const WIZARD_STEPS = ['Select Model', 'Device Info', 'Extension', 'Provisioning', 'Confirm'];
const PROV_BASE = '/provisioning';

// ── Helpers ──────────────────────────────────────────────────────────────────

function formatMac(raw: string) {
  const m = raw.replace(/[^0-9a-fA-F]/g, '').toUpperCase();
  return m.match(/.{2}/g)?.join(':') || raw;
}

function timeSince(ts?: string) {
  if (!ts) return 'Never';
  const diff = Date.now() - new Date(ts).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return 'Just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

function statusBadge(phone: Phone) {
  if (!phone.provisioning_enabled) return <span style={badge('#6b7280')}>Disabled</span>;
  // Live registration from the phone system (refreshed every 30 s)
  if (phone.sip_registered) return <span style={badge('#10b981')} title={`${phone.sip_state || ''} from ${phone.sip_contact_ip || ''}${phone.sip_rtt_ms != null ? ` · ${Math.round(phone.sip_rtt_ms)} ms` : ''}`}>Registered</span>;
  if (phone.sip_state && phone.sip_state !== 'none') return <span style={badge('#f59e0b')} title={`Registered from ${phone.sip_contact_ip || ''} but not answering checks`}>Unreachable</span>;
  if (!phone.last_checkin) return <span style={badge('#f59e0b')}>Pending</span>;
  return <span style={badge('#ef4444')}>Not registered</span>;
}

function badge(color: string): React.CSSProperties {
  return {
    background: color, color: '#fff', padding: '2px 10px',
    borderRadius: 12, fontSize: 11, fontWeight: 700, display: 'inline-block',
  };
}

// ── Main Component ────────────────────────────────────────────────────────────

export default function PhonesPage({ user }: { user: any }) {
  const isPlatformAdmin = !!(user?.is_admin || user?.role === 'super_admin');

  const [tab, setTab] = useState<'phones' | 'dashboard' | 'firmware' | 'audit'>('phones');
  const [phones, setPhones] = useState<Phone[]>([]);
  const [dashboard, setDashboard] = useState<Dashboard | null>(null);
  const [models, setModels] = useState<PhoneModel[]>([]);
  const [extensions, setExtensions] = useState<Extension[]>([]);
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [firmware, setFirmware] = useState<FirmwareRow[]>([]);
  const [auditLogs, setAuditLogs] = useState<AuditRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [showWizard, setShowWizard] = useState(false);
  const [_editPhone, _setEditPhone] = useState<Phone | null>(null); // reserved for edit modal
  const [xmlPreview, setXmlPreview] = useState<{ phoneId: number; xml: string } | null>(null);
  const [generatedUrl, setGeneratedUrl] = useState<{ phoneId: number; url: string; filename: string } | null>(null);
  const [statusMap, setStatusMap] = useState<Record<number, any>>({});
  const [search, setSearch] = useState('');

  // Wizard state
  const [wizStep, setWizStep] = useState(0);
  const [wizModel, setWizModel] = useState('CP-8811');
  const [wizName, setWizName] = useState('');
  const [wizMac, setWizMac] = useState('');
  const [wizMacErr, setWizMacErr] = useState('');
  const [wizExt, setWizExt] = useState<number | ''>('');
  const [wizProvEnabled, setWizProvEnabled] = useState(true);
  const [wizTenant, setWizTenant] = useState<number | ''>(isPlatformAdmin ? '' : (user?.tenant_id || ''));
  const [wizSaving, setWizSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [ph, mod, exts] = await Promise.all([
        api.getPhones(),
        api.getPhoneModels(),
        api.getExtensions(),
      ]);
      setPhones(ph);
      setModels(mod);
      setExtensions(exts);
      if (isPlatformAdmin) {
        const t = await api.getTenants();
        setTenants(t);
      }
    } catch (e: any) {
      setError(e?.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  }, [isPlatformAdmin]);

  useEffect(() => { load(); }, [load]);
  // Keep the registration status live while the page is open
  useEffect(() => {
    const t = setInterval(() => { api.getPhones().then(setPhones).catch(() => {}); }, 30000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    if (tab === 'dashboard' && !dashboard) {
      api.getPhonesDashboard().then(setDashboard).catch(() => {});
    }
    if (tab === 'firmware' && firmware.length === 0) {
      api.getFirmwareList().then(setFirmware).catch(() => {});
    }
    if (tab === 'audit' && auditLogs.length === 0) {
      api.getPhoneAuditLogs(undefined, 200).then(setAuditLogs).catch(() => {});
    }
  }, [tab, dashboard, firmware.length, auditLogs.length]);

  // ── Actions ──────────────────────────────────────────────────────────────

  async function handleDelete(phone: Phone) {
    if (!window.confirm(`Delete phone "${phone.device_name}" (${phone.mac_address})?`)) return;
    try {
      await api.deletePhone(phone.id);
      setPhones(prev => prev.filter(p => p.id !== phone.id));
    } catch (e: any) {
      alert(e?.response?.data?.error || 'Delete failed');
    }
  }

  async function handleGenerate(phone: Phone) {
    try {
      const result = await api.generatePhoneConfig(phone.id);
      setGeneratedUrl({ phoneId: phone.id, url: result.provisioning_url, filename: result.filename });
      setXmlPreview({ phoneId: phone.id, xml: result.xml });
      setPhones(prev => prev.map(p => p.id === phone.id ? { ...p, provisioning_enabled: true } : p));
    } catch (e: any) {
      alert(e?.response?.data?.error || 'Generate failed');
    }
  }

  async function handleViewConfig(phone: Phone) {
    try {
      const xml = await api.getPhoneConfig(phone.id);
      setXmlPreview({ phoneId: phone.id, xml: typeof xml === 'string' ? xml : JSON.stringify(xml, null, 2) });
    } catch (e: any) {
      alert(e?.response?.data?.error || 'Could not load config');
    }
  }

  async function handleReboot(phone: Phone) {
    if (!window.confirm(`Send reboot signal to "${phone.device_name}"?`)) return;
    try {
      const result = await api.rebootPhone(phone.id);
      alert(result.ok ? 'Reboot signal sent successfully.' : `Reboot failed: ${result.error}`);
    } catch (e: any) {
      alert(e?.response?.data?.error || 'Reboot failed');
    }
  }

  async function handleFetchStatus(phone: Phone) {
    try {
      const s = await api.getPhoneStatus(phone.id);
      setStatusMap(prev => ({ ...prev, [phone.id]: s }));
    } catch (_) {
      setStatusMap(prev => ({ ...prev, [phone.id]: { state: 'error' } }));
    }
  }

  async function handleToggleProvisioning(phone: Phone) {
    try {
      await api.updatePhone(phone.id, { provisioning_enabled: !phone.provisioning_enabled });
      setPhones(prev => prev.map(p => p.id === phone.id
        ? { ...p, provisioning_enabled: !p.provisioning_enabled } : p));
    } catch (e: any) {
      alert(e?.response?.data?.error || 'Update failed');
    }
  }

  // ── Wizard ────────────────────────────────────────────────────────────────

  function openWizard() {
    setWizStep(0); setWizModel('CP-8811'); setWizName(''); setWizMac('');
    setWizMacErr(''); setWizExt(''); setWizProvEnabled(true);
    setWizTenant(isPlatformAdmin ? '' : (user?.tenant_id || ''));
    setShowWizard(true);
  }

  function validateMacInput(v: string) {
    const clean = v.replace(/[^0-9a-fA-F]/g, '');
    return clean.length === 12;
  }

  async function handleWizardSave() {
    setWizSaving(true);
    try {
      const phone = await api.createPhone({
        device_name: wizName,
        mac_address: wizMac,
        model: wizModel,
        extension_id: wizExt || null,
        provisioning_enabled: wizProvEnabled,
        tenant_id: wizTenant || undefined,
      });
      setShowWizard(false);
      setPhones(prev => [phone, ...prev]);

      if (wizProvEnabled && wizExt) {
        const result = await api.generatePhoneConfig(phone.id);
        setGeneratedUrl({ phoneId: phone.id, url: result.provisioning_url, filename: result.filename });
        setXmlPreview({ phoneId: phone.id, xml: result.xml });
        setPhones(prev => prev.map(p => p.id === phone.id ? { ...p, provisioning_enabled: true } : p));
      }
    } catch (e: any) {
      alert(e?.response?.data?.error || 'Save failed');
    } finally {
      setWizSaving(false);
    }
  }

  // ── Filtered phones ───────────────────────────────────────────────────────

  const filtered = phones.filter(p => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      p.device_name.toLowerCase().includes(q) ||
      p.mac_address.toLowerCase().includes(q) ||
      (p.extension_number || '').includes(q) ||
      p.model.toLowerCase().includes(q) ||
      (p.tenant_name || '').toLowerCase().includes(q)
    );
  });

  const tenantExtensions = isPlatformAdmin && wizTenant
    ? extensions.filter(e => e.tenant_id === Number(wizTenant))
    : !isPlatformAdmin
      ? extensions.filter(e => e.tenant_id === user?.tenant_id)
      : extensions;

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div style={{ padding: '24px' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: '#1f2937' }}>📞 Phone Provisioning</h2>
          <div style={{ color: '#6b7280', fontSize: 13, marginTop: 2 }}>
            Cisco IP Phone auto-provisioning &amp; management
          </div>
        </div>
        <button onClick={openWizard} style={btnStyle('#6366f1')}>+ Add Phone</button>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 20, borderBottom: '2px solid #e5e7eb' }}>
        {(['phones', 'dashboard', 'firmware', 'audit'] as const).map(t => (
          <button key={t} onClick={() => setTab(t)} style={{
            padding: '8px 18px', border: 'none', background: 'none',
            borderBottom: tab === t ? '2px solid #6366f1' : '2px solid transparent',
            marginBottom: -2, color: tab === t ? '#6366f1' : '#6b7280',
            fontWeight: tab === t ? 700 : 500, cursor: 'pointer', textTransform: 'capitalize',
          }}>
            {t === 'phones' ? 'Phones' : t === 'dashboard' ? 'Dashboard' : t === 'firmware' ? 'Firmware' : 'Audit Log'}
          </button>
        ))}
      </div>

      {error && <div style={{ color: '#ef4444', marginBottom: 12 }}>{error}</div>}

      {/* ── PHONES TAB ── */}
      {tab === 'phones' && (
        <div>
          <div style={{ marginBottom: 14 }}>
            <input
              placeholder="Search by name, MAC, extension, model…"
              value={search} onChange={e => setSearch(e.target.value)}
              style={{ padding: '8px 14px', border: '1px solid #d1d5db', borderRadius: 8, width: 340, fontSize: 14 }}
            />
            <span style={{ marginLeft: 12, color: '#6b7280', fontSize: 13 }}>
              {filtered.length} phone{filtered.length !== 1 ? 's' : ''}
            </span>
          </div>

          {loading ? (
            <div style={{ color: '#6b7280', padding: 40, textAlign: 'center' }}>Loading…</div>
          ) : filtered.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 60, color: '#9ca3af' }}>
              <div style={{ fontSize: 40, marginBottom: 12 }}>📞</div>
              <div style={{ fontWeight: 600 }}>No phones registered yet</div>
              <div style={{ fontSize: 13, marginTop: 6 }}>Click <strong>+ Add Phone</strong> to register your first Cisco IP phone</div>
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ background: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
                    {['Device Name', 'MAC Address', 'Model', 'Extension', isPlatformAdmin ? 'Tenant' : '', 'Status', 'Last provisioned', 'Firmware', 'Actions'].filter(Boolean).map(h => (
                      <th key={h} style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 700, color: '#374151', fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(phone => (
                    <tr key={phone.id} style={{ borderBottom: '1px solid #f3f4f6' }}
                      onMouseEnter={e => (e.currentTarget.style.background = '#f9fafb')}
                      onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}>
                      <td style={{ padding: '10px 12px', fontWeight: 600, color: '#1f2937' }}>{phone.device_name}</td>
                      <td style={{ padding: '10px 12px', fontFamily: 'monospace', color: '#374151' }}>{phone.mac_address}</td>
                      <td style={{ padding: '10px 12px', color: '#374151' }}>
                        <span style={{ background: '#ede9fe', color: '#6d28d9', padding: '2px 8px', borderRadius: 6, fontSize: 11, fontWeight: 700 }}>{phone.model}</span>
                      </td>
                      <td style={{ padding: '10px 12px', color: '#374151' }}>
                        {phone.extension_number
                          ? <><strong>{phone.extension_number}</strong>{phone.extension_display ? ` — ${phone.extension_display}` : ''}</>
                          : <span style={{ color: '#9ca3af' }}>—</span>}
                      </td>
                      {isPlatformAdmin && <td style={{ padding: '10px 12px', color: '#6b7280', fontSize: 12 }}>{phone.tenant_name || '—'}</td>}
                      <td style={{ padding: '10px 12px' }}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                          {statusBadge(phone)}
                          {statusMap[phone.id] && (
                            <span style={{ fontSize: 10, color: '#6b7280' }}>
                              PJSIP: {statusMap[phone.id].state}
                            </span>
                          )}
                        </div>
                      </td>
                      <td style={{ padding: '10px 12px', color: '#6b7280', fontSize: 12 }}>{timeSince(phone.last_checkin)}</td>
                      <td style={{ padding: '10px 12px', color: '#6b7280', fontSize: 11, fontFamily: 'monospace' }}>
                        {phone.firmware_version || <span style={{ color: '#d1d5db' }}>Auto</span>}
                      </td>
                      <td style={{ padding: '10px 12px' }}>
                        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                          <button onClick={() => handleGenerate(phone)} style={smallBtn('#6366f1')} title="Generate & enable provisioning">Generate</button>
                          <button onClick={() => handleViewConfig(phone)} style={smallBtn('#0ea5e9')} title="Preview XML config">Config</button>
                          <button onClick={() => handleFetchStatus(phone)} style={smallBtn('#8b5cf6')} title="Check PJSIP status">Status</button>
                          <button onClick={() => handleReboot(phone)} style={smallBtn('#f59e0b')} title="Send reboot">Reboot</button>
                          <button onClick={() => handleToggleProvisioning(phone)}
                            style={smallBtn(phone.provisioning_enabled ? '#6b7280' : '#10b981')}
                            title={phone.provisioning_enabled ? 'Disable provisioning' : 'Enable provisioning'}>
                            {phone.provisioning_enabled ? 'Disable' : 'Enable'}
                          </button>
                          <button onClick={() => handleDelete(phone)} style={smallBtn('#ef4444')} title="Delete phone">Delete</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ── DASHBOARD TAB ── */}
      {tab === 'dashboard' && (
        <div>
          {!dashboard ? (
            <div style={{ color: '#6b7280', padding: 40, textAlign: 'center' }}>Loading dashboard…</div>
          ) : (
            <div>
              {/* Stat cards */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 16, marginBottom: 28 }}>
                {[
                  { label: 'Total Phones', value: dashboard.totals.total, color: '#6366f1', icon: '📞' },
                  { label: 'Provisioned', value: dashboard.totals.provisioned, color: '#10b981', icon: '✅' },
                  { label: 'Online (1h)', value: dashboard.totals.recently_seen, color: '#0ea5e9', icon: '🟢' },
                  { label: 'Never Checked In', value: dashboard.totals.never_checked_in, color: '#f59e0b', icon: '⚠️' },
                ].map(s => (
                  <div key={s.label} style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: '20px 24px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
                    <div style={{ fontSize: 28, marginBottom: 8 }}>{s.icon}</div>
                    <div style={{ fontSize: 28, fontWeight: 800, color: s.color }}>{s.value}</div>
                    <div style={{ fontSize: 12, color: '#6b7280', fontWeight: 600, marginTop: 4 }}>{s.label}</div>
                  </div>
                ))}
              </div>

              {/* Tables */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
                <DashTable title="By Model" rows={dashboard.by_model} keyField="model" countField="count" />
                <DashTable title="By Firmware" rows={dashboard.by_firmware} keyField="firmware_version" countField="count" />
                {isPlatformAdmin && dashboard.by_tenant.length > 0 && (
                  <DashTable title="By Tenant" rows={dashboard.by_tenant} keyField="tenant_name" countField="count" />
                )}
              </div>

              {/* Recent check-ins */}
              {dashboard.recent_checkins.length > 0 && (
                <div style={{ marginTop: 24 }}>
                  <h3 style={{ fontSize: 14, fontWeight: 700, color: '#374151', marginBottom: 12 }}>Recent Check-ins</h3>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                    <thead>
                      <tr style={{ background: '#f9fafb' }}>
                        {['Device', 'MAC', 'Model', 'Extension', 'Last Seen'].map(h => (
                          <th key={h} style={{ padding: '8px 12px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {dashboard.recent_checkins.map((p, i) => (
                        <tr key={i} style={{ borderBottom: '1px solid #f3f4f6' }}>
                          <td style={{ padding: '8px 12px', fontWeight: 600 }}>{p.device_name}</td>
                          <td style={{ padding: '8px 12px', fontFamily: 'monospace', fontSize: 12 }}>{p.mac_address}</td>
                          <td style={{ padding: '8px 12px' }}>{p.model}</td>
                          <td style={{ padding: '8px 12px' }}>{p.extension_number || '—'}</td>
                          <td style={{ padding: '8px 12px', color: '#6b7280' }}>{timeSince(p.last_checkin)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ── FIRMWARE TAB ── */}
      {tab === 'firmware' && (
        <div>
          <div style={{ marginBottom: 16, color: '#6b7280', fontSize: 13, background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 8, padding: '10px 14px' }}>
            <strong>Firmware files</strong> are served at <code>{PROV_BASE}/firmware/&lt;filename&gt;</code>.
            Upload Cisco <code>.loads</code> or <code>.sbn</code> files. The phone will auto-download based on the <code>&lt;loadInformation&gt;</code> in its config.
          </div>

          {isPlatformAdmin && (
            <FirmwareUpload onUploaded={fw => setFirmware(prev => [fw, ...prev])} />
          )}

          {firmware.length === 0 ? (
            <div style={{ color: '#9ca3af', padding: 32, textAlign: 'center' }}>No firmware files uploaded yet</div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: '#f9fafb' }}>
                  {['Model', 'Version', 'Filename', 'Size', isPlatformAdmin ? 'Actions' : ''].filter(Boolean).map(h => (
                    <th key={h} style={{ padding: '8px 12px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {firmware.map(fw => (
                  <tr key={fw.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                    <td style={{ padding: '8px 12px' }}>
                      <span style={{ background: '#ede9fe', color: '#6d28d9', padding: '2px 8px', borderRadius: 6, fontSize: 11, fontWeight: 700 }}>{fw.model}</span>
                    </td>
                    <td style={{ padding: '8px 12px', fontFamily: 'monospace' }}>{fw.firmware_version}</td>
                    <td style={{ padding: '8px 12px', fontFamily: 'monospace', fontSize: 12 }}>{fw.file_name}</td>
                    <td style={{ padding: '8px 12px', color: '#6b7280' }}>
                      {fw.file_size ? `${(fw.file_size / 1024 / 1024).toFixed(1)} MB` : '—'}
                    </td>
                    {isPlatformAdmin && (
                      <td style={{ padding: '8px 12px' }}>
                        <button onClick={async () => {
                          if (!window.confirm('Delete this firmware?')) return;
                          await api.deleteFirmware(fw.id);
                          setFirmware(prev => prev.filter(f => f.id !== fw.id));
                        }} style={smallBtn('#ef4444')}>Delete</button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* ── AUDIT TAB ── */}
      {tab === 'audit' && (
        <div>
          {auditLogs.length === 0 ? (
            <div style={{ color: '#9ca3af', padding: 32, textAlign: 'center' }}>No audit events yet</div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: '#f9fafb' }}>
                  {['Timestamp', 'Action', 'User', 'IP', 'Details'].map(h => (
                    <th key={h} style={{ padding: '8px 12px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {auditLogs.map(log => (
                  <tr key={log.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                    <td style={{ padding: '8px 12px', color: '#6b7280', fontFamily: 'monospace', fontSize: 11 }}>
                      {new Date(log.created_at).toLocaleString()}
                    </td>
                    <td style={{ padding: '8px 12px' }}>
                      <span style={{ background: actionColor(log.action), color: '#fff', padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 700 }}>
                        {log.action.replace(/_/g, ' ')}
                      </span>
                    </td>
                    <td style={{ padding: '8px 12px', color: '#374151' }}>{log.username || '—'}</td>
                    <td style={{ padding: '8px 12px', fontFamily: 'monospace', fontSize: 11, color: '#6b7280' }}>{log.ip_address || '—'}</td>
                    <td style={{ padding: '8px 12px', color: '#6b7280', fontSize: 12 }}>
                      {log.details ? JSON.stringify(log.details) : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* ── ADD PHONE WIZARD ── */}
      {showWizard && (
        <Modal title="Add Cisco IP Phone" onClose={() => setShowWizard(false)} width={560}>
          {/* Step indicator */}
          <div style={{ display: 'flex', gap: 0, marginBottom: 28, borderRadius: 8, overflow: 'hidden', border: '1px solid #e5e7eb' }}>
            {WIZARD_STEPS.map((s, i) => (
              <div key={s} style={{
                flex: 1, padding: '8px 4px', textAlign: 'center', fontSize: 11, fontWeight: 700,
                background: i === wizStep ? '#6366f1' : i < wizStep ? '#e0e7ff' : '#f9fafb',
                color: i === wizStep ? '#fff' : i < wizStep ? '#6366f1' : '#9ca3af',
                borderRight: i < WIZARD_STEPS.length - 1 ? '1px solid #e5e7eb' : 'none',
              }}>{i + 1}. {s}</div>
            ))}
          </div>

          {/* Step 0: Model */}
          {wizStep === 0 && (
            <div>
              <label style={labelStyle}>Select Phone Model</label>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 8 }}>
                {models.map(m => (
                  <div key={m.model} onClick={() => setWizModel(m.model)}
                    style={{ border: `2px solid ${wizModel === m.model ? '#6366f1' : '#e5e7eb'}`, borderRadius: 10, padding: '12px 16px', cursor: 'pointer', background: wizModel === m.model ? '#ede9fe' : '#fff', transition: 'all 0.15s' }}>
                    <div style={{ fontWeight: 700, color: wizModel === m.model ? '#6366f1' : '#374151' }}>{m.model}</div>
                    <div style={{ fontSize: 11, color: '#6b7280', marginTop: 2 }}>{m.lines} line{m.lines !== 1 ? 's' : ''}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Step 1: Device info */}
          {wizStep === 1 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {isPlatformAdmin && (
                <div>
                  <label style={labelStyle}>Tenant *</label>
                  <select value={wizTenant} onChange={e => { setWizTenant(Number(e.target.value)); setWizExt(''); }} style={inputStyle}>
                    <option value="">— Select Tenant —</option>
                    {tenants.map(t => <option key={t.id} value={t.id}>{t.tenant_name}</option>)}
                  </select>
                </div>
              )}
              <div>
                <label style={labelStyle}>Device Name *</label>
                <input value={wizName} onChange={e => setWizName(e.target.value)} placeholder="e.g. Reception Desk" style={inputStyle} />
              </div>
              <div>
                <label style={labelStyle}>MAC Address *</label>
                <input value={wizMac}
                  onChange={e => { setWizMac(e.target.value); setWizMacErr(''); }}
                  onBlur={() => { if (wizMac && !validateMacInput(wizMac)) setWizMacErr('Enter 12 hex digits (with or without colons/hyphens)'); }}
                  placeholder="e.g. AA:BB:CC:DD:EE:FF" style={{ ...inputStyle, borderColor: wizMacErr ? '#ef4444' : '#d1d5db' }} />
                {wizMacErr && <div style={{ color: '#ef4444', fontSize: 12, marginTop: 4 }}>{wizMacErr}</div>}
                <div style={{ fontSize: 11, color: '#6b7280', marginTop: 4 }}>Found on the phone label or web admin. Format: AABBCCDDEEFF or AA:BB:CC:DD:EE:FF</div>
              </div>
            </div>
          )}

          {/* Step 2: Extension */}
          {wizStep === 2 && (
            <div>
              <label style={labelStyle}>Assign Extension</label>
              <select value={wizExt} onChange={e => setWizExt(e.target.value ? Number(e.target.value) : '')} style={inputStyle}>
                <option value="">— No extension —</option>
                {tenantExtensions.map(e => (
                  <option key={e.id} value={e.id}>{e.extension_number} — {e.display_name}</option>
                ))}
              </select>
              {tenantExtensions.length === 0 && (
                <div style={{ color: '#f59e0b', fontSize: 12, marginTop: 8 }}>
                  {isPlatformAdmin && !wizTenant ? 'Select a tenant first.' : 'No extensions available. Create extensions in the Extensions page.'}
                </div>
              )}
            </div>
          )}

          {/* Step 3: Provisioning */}
          {wizStep === 3 && (
            <div>
              <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 10, padding: '16px 18px', marginBottom: 16 }}>
                <div style={{ fontWeight: 700, color: '#166534', marginBottom: 6 }}>How provisioning works</div>
                <div style={{ fontSize: 13, color: '#166534', lineHeight: 1.6 }}>
                  After saving, set your phone's <strong>Profile Server URL</strong> to:<br />
                  <code style={{ background: '#dcfce7', padding: '2px 6px', borderRadius: 4 }}>{PROV_BASE}/</code><br /><br />
                  The phone will request <code>SEP&lt;MAC&gt;.xml</code> and receive its SIP config automatically.
                </div>
              </div>
              <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', fontSize: 14, fontWeight: 600, color: '#374151' }}>
                <input type="checkbox" checked={wizProvEnabled} onChange={e => setWizProvEnabled(e.target.checked)}
                  style={{ width: 18, height: 18, cursor: 'pointer' }} />
                Enable provisioning immediately
              </label>
              <div style={{ fontSize: 12, color: '#6b7280', marginTop: 6, marginLeft: 28 }}>
                When enabled, the phone can download its config. Disable to prevent provisioning until ready.
              </div>
            </div>
          )}

          {/* Step 4: Confirm */}
          {wizStep === 4 && (
            <div style={{ fontSize: 13 }}>
              <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 10, padding: 18 }}>
                {([
                  ['Model', wizModel],
                  ['Device Name', wizName],
                  ['MAC Address', formatMac(wizMac)],
                  ['Extension', wizExt ? (tenantExtensions.find(e => e.id === Number(wizExt))?.extension_number || String(wizExt)) : 'None'],
                  ['Provisioning', wizProvEnabled ? 'Enabled' : 'Disabled'],
                  ...(isPlatformAdmin ? [['Tenant', tenants.find(t => t.id === Number(wizTenant))?.tenant_name || '—']] : []),
                ] as [string, string][]).map(([label, val]) => (
                  <div key={label} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid #f3f4f6' }}>
                    <span style={{ color: '#6b7280', fontWeight: 600 }}>{label}</span>
                    <span style={{ color: '#1f2937', fontWeight: 700 }}>{val}</span>
                  </div>
                ))}
              </div>
              <div style={{ color: '#6b7280', fontSize: 12, marginTop: 12 }}>
                Clicking <strong>Save &amp; Generate</strong> will register this phone and {wizProvEnabled ? 'generate its provisioning config on the PBX.' : 'save it in disabled state.'}
              </div>
            </div>
          )}

          {/* Wizard nav */}
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 28 }}>
            <button onClick={() => wizStep > 0 ? setWizStep(w => w - 1) : setShowWizard(false)}
              style={btnStyle('#6b7280')}>
              {wizStep === 0 ? 'Cancel' : '← Back'}
            </button>
            {wizStep < WIZARD_STEPS.length - 1 ? (
              <button onClick={() => {
                if (wizStep === 1) {
                  if (!wizName.trim()) return alert('Enter a device name');
                  if (!validateMacInput(wizMac)) { setWizMacErr('Invalid MAC address'); return; }
                }
                setWizStep(w => w + 1);
              }} style={btnStyle('#6366f1')}>
                Next →
              </button>
            ) : (
              <button onClick={handleWizardSave} disabled={wizSaving} style={btnStyle('#10b981')}>
                {wizSaving ? 'Saving…' : 'Save & Generate'}
              </button>
            )}
          </div>
        </Modal>
      )}

      {/* ── XML PREVIEW MODAL ── */}
      {xmlPreview && (
        <Modal title="Provisioning XML Config" onClose={() => setXmlPreview(null)} width={700}>
          {generatedUrl && generatedUrl.phoneId === xmlPreview.phoneId && (
            <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, padding: '12px 16px', marginBottom: 16, fontSize: 13 }}>
              <strong>✅ Config generated!</strong> Provisioning URL:<br />
              <code style={{ wordBreak: 'break-all', color: '#166534' }}>{generatedUrl.url}</code>
              <div style={{ marginTop: 6, color: '#6b7280', fontSize: 12 }}>
                Set this as your phone's <strong>Profile Server URL</strong> (or just set <code>{PROV_BASE}/</code> and the phone will find its config by MAC).
              </div>
            </div>
          )}
          <pre style={{
            background: '#1e293b', color: '#e2e8f0', padding: 16, borderRadius: 8,
            overflowX: 'auto', fontSize: 12, lineHeight: 1.5, maxHeight: 420, overflowY: 'auto',
          }}>
            {xmlPreview.xml}
          </pre>
          <div style={{ display: 'flex', gap: 10, marginTop: 14, justifyContent: 'flex-end' }}>
            <button onClick={() => { navigator.clipboard.writeText(xmlPreview.xml); }}
              style={btnStyle('#6366f1')}>Copy XML</button>
            <button onClick={() => setXmlPreview(null)} style={btnStyle('#6b7280')}>Close</button>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ── Sub-components ────────────────────────────────────────────────────────────

function Modal({ title, onClose, width = 500, children }: { title: string; onClose: () => void; width?: number; children: React.ReactNode }) {
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div style={{ background: '#fff', borderRadius: 16, padding: '28px 32px', width: '100%', maxWidth: width, maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 24px 80px rgba(0,0,0,0.3)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 22 }}>
          <h3 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: '#1f2937' }}>{title}</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: '#6b7280', lineHeight: 1 }}>×</button>
        </div>
        {children}
      </div>
    </div>
  );
}

function DashTable({ title, rows, keyField, countField }: { title: string; rows: any[]; keyField: string; countField: string }) {
  return (
    <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: '16px 20px' }}>
      <h3 style={{ margin: '0 0 14px', fontSize: 14, fontWeight: 700, color: '#374151' }}>{title}</h3>
      {rows.length === 0 ? (
        <div style={{ color: '#9ca3af', fontSize: 13 }}>No data</div>
      ) : (
        rows.map((row, i) => (
          <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', borderBottom: i < rows.length - 1 ? '1px solid #f3f4f6' : 'none' }}>
            <span style={{ fontSize: 13, color: '#374151' }}>{row[keyField]}</span>
            <span style={{ fontWeight: 800, color: '#6366f1', fontSize: 16 }}>{row[countField]}</span>
          </div>
        ))
      )}
    </div>
  );
}

function FirmwareUpload({ onUploaded }: { onUploaded: (fw: any) => void }) {
  const [model, setModel] = useState('CP-8811');
  const [version, setVersion] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);

  async function upload() {
    if (!model || !version || !file) return alert('Fill all fields');
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('firmware', file);
      fd.append('model', model);
      fd.append('firmware_version', version);
      const fw = await api.uploadFirmware(fd);
      onUploaded(fw);
      setVersion(''); setFile(null);
    } catch (e: any) {
      alert(e?.response?.data?.error || 'Upload failed');
    } finally {
      setUploading(false);
    }
  }

  return (
    <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 10, padding: '16px 18px', marginBottom: 20 }}>
      <div style={{ fontWeight: 700, fontSize: 13, color: '#374151', marginBottom: 12 }}>Upload Firmware</div>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <div>
          <label style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', display: 'block', marginBottom: 4 }}>Model</label>
          <select value={model} onChange={e => setModel(e.target.value)} style={{ padding: '7px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13 }}>
            {['CP-8811','CP-8841','CP-8851','CP-8861','CP-8865','CP-7811','CP-7821','CP-7841','CP-7861'].map(m => <option key={m} value={m}>{m}</option>)}
          </select>
        </div>
        <div>
          <label style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', display: 'block', marginBottom: 4 }}>Version</label>
          <input value={version} onChange={e => setVersion(e.target.value)} placeholder="e.g. 14-1-1SR1" style={{ padding: '7px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, width: 140 }} />
        </div>
        <div>
          <label style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', display: 'block', marginBottom: 4 }}>File (.loads/.sbn)</label>
          <input type="file" accept=".loads,.sbn,.bin,.cop,.sgn" onChange={e => setFile(e.target.files?.[0] || null)} style={{ fontSize: 13 }} />
        </div>
        <button onClick={upload} disabled={uploading} style={btnStyle('#6366f1')}>
          {uploading ? 'Uploading…' : 'Upload'}
        </button>
      </div>
    </div>
  );
}

// ── Style helpers ─────────────────────────────────────────────────────────────

function btnStyle(color: string): React.CSSProperties {
  return {
    background: color, color: '#fff', border: 'none', borderRadius: 8,
    padding: '9px 18px', fontSize: 13, fontWeight: 700, cursor: 'pointer',
  };
}

function smallBtn(color: string): React.CSSProperties {
  return {
    background: color, color: '#fff', border: 'none', borderRadius: 6,
    padding: '4px 10px', fontSize: 11, fontWeight: 700, cursor: 'pointer',
  };
}

const labelStyle: React.CSSProperties = {
  display: 'block', fontSize: 12, fontWeight: 700,
  color: '#374151', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.5px',
};

const inputStyle: React.CSSProperties = {
  width: '100%', padding: '9px 12px', border: '1px solid #d1d5db',
  borderRadius: 8, fontSize: 14, boxSizing: 'border-box',
};

function actionColor(action: string) {
  if (action.includes('deleted') || action.includes('denied')) return '#ef4444';
  if (action.includes('rebooted'))  return '#f59e0b';
  if (action.includes('generated') || action.includes('downloaded')) return '#10b981';
  if (action.includes('created'))   return '#6366f1';
  if (action.includes('updated'))   return '#0ea5e9';
  return '#6b7280';
}
